/** Shared pieces of the Reports tab (item 63): dates and schedules in the
 *  app's locale, the KPI row's figures, the library's grouping, and a refused
 *  request in the person's own language. Every number shown here is one the
 *  server computed (report_facts); nothing is recomputed in the browser. */
import { activeLocale } from '@/utils/locale';
import type { TFunc as T } from '@/utils/tfunc';
import { errorCode, formatCount, formatUsd } from '../templates/templateParts';
import type { Compared, LibraryReport, ReportTemplate, ReportWindow } from '../../types/insights';


const LRI = '\u2066';
const PDI = '\u2069';
/** A figure kept left to right inside any line: in a right-to-left one the dash
 *  of "19–25" and the × of "×13.2" otherwise turn round ("25–19", "13.2×"). */
const ltr = (text: string) => `${LRI}${text}${PDI}`;

/** 0 = Monday ... 6 = Sunday (the server's order). */
export const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];
/** The order a week is shown in: Sunday first in Hebrew, Monday first otherwise. */
export function weekOrder(language: string): number[] {
  return language === 'he' ? [6, 0, 1, 2, 3, 4, 5] : WEEKDAYS;
}

/** A Monday-based weekday's name in the app's language ('short' | 'long'). */
export function weekdayName(day: number, width: 'short' | 'long' = 'short'): string {
  // 2026-11-02 is a Monday.
  const date = new Date(Date.UTC(2026, 10, 2 + day, 12));
  return new Intl.DateTimeFormat(activeLocale(), { weekday: width, timeZone: 'UTC' }).format(date);
}

export function utcDate(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
}

/** "19–25 Oct 2026", "September 2026": the days a report covers. */
export function windowLabel(window: ReportWindow | null, options: { year?: boolean } = {}): string {
  if (!window) return '';
  const start = utcDate(window.start);
  const end = utcDate(window.end);
  const lastDay = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 0, 12)).getUTCDate();
  if (start.getUTCDate() === 1 && end.getUTCDate() === lastDay && start.getUTCMonth() === end.getUTCMonth()
      && start.getUTCFullYear() === end.getUTCFullYear()) {
    return new Intl.DateTimeFormat(activeLocale(), { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(start);
  }
  return formatDayRange(start, end, options.year !== false);
}

/** Two UTC days as one range in the app's locale ("19–25 Oct 2026", "19–25 באוק׳ 2026"). */
export function formatDayRange(start: Date, end: Date, year = true): string {
  // formatRange is in every browser the app supports, but not in this
  // project's TypeScript lib yet.
  const format = new Intl.DateTimeFormat(activeLocale(), {
    day: 'numeric', month: 'short', ...(year ? { year: 'numeric' } : {}), timeZone: 'UTC',
  }) as Intl.DateTimeFormat & { formatRange(a: Date, b: Date): string };
  const text = start.getTime() === end.getTime() ? format.format(start) : format.formatRange(start, end);
  return text.replace(/\d+\s*[–-]\s*\d+/g, ltr);
}

/** An instant in a zone: "Mon 2 Nov, 08:00". */
export function instantLabel(iso: string | null, zone?: string): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat(activeLocale(), {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    ...(zone ? { timeZone: zone } : {}),
  }).format(new Date(iso));
}

/** Just the time of day ("08:04"), or the date for an older one ("26 Oct"). */
export function whenLabel(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '';
  const at = new Date(iso);
  const sameDay = at.toDateString() === now.toDateString();
  return new Intl.DateTimeFormat(activeLocale(), sameDay
    ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
    : { day: 'numeric', month: 'short' }).format(at);
}

/** "Weekly · Mon 08:00", "Daily · 07:30", "Monthly · day 1, 09:00", "Run by hand". */
export function scheduleLabel(t: T, template: Pick<ReportTemplate, 'frequency' | 'weekdays' | 'month_day' | 'run_time'>,
                              language: string): string {
  const at = template.run_time;
  if (template.frequency === 'daily') {
    const days = template.weekdays || [];
    const which = days.length && days.length < 7
      ? weekOrder(language).filter((d) => days.includes(d)).map((d) => weekdayName(d)).join(', ')
      : '';
    return t('insights.reports.schedule.dailyAt', { time: at, days: which ? ` · ${which}` : '' });
  }
  if (template.frequency === 'weekly') {
    return t('insights.reports.schedule.weeklyAt', { day: weekdayName(template.weekdays[0] ?? 0), time: at });
  }
  if (template.frequency === 'monthly') {
    return t('insights.reports.schedule.monthlyAt', { day: template.month_day ?? 1, time: at });
  }
  return t('insights.reports.schedule.byHand');
}

/** Today / This week / Earlier, newest first (the library's groups). */
export function groupByRecency(reports: LibraryReport[], now: Date = new Date()):
  { key: 'today' | 'week' | 'earlier'; reports: LibraryReport[] }[] {
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const weekAgo = startOfDay - 6 * 24 * 3600 * 1000;
  const groups = { today: [] as LibraryReport[], week: [] as LibraryReport[], earlier: [] as LibraryReport[] };
  for (const report of reports) {
    const at = new Date(report.created_at || 0).getTime();
    groups[at >= startOfDay ? 'today' : at >= weekAgo ? 'week' : 'earlier'].push(report);
  }
  return (['today', 'week', 'earlier'] as const)
    .map((key) => ({ key, reports: groups[key] }))
    .filter((g) => g.reports.length);
}

// ---- the KPI row -------------------------------------------------------------------

export type KpiKind = 'count' | 'percent' | 'money';

export function kpiValue(c: Compared | undefined, kind: KpiKind): string {
  const value = c?.current;
  if (value === null || value === undefined) return '—';
  if (kind === 'percent') return `${new Intl.NumberFormat(activeLocale(), { maximumFractionDigits: 1 }).format(value)}%`;
  if (kind === 'money') return formatUsd(value);
  return formatCount(value);
}

/** The change as the report states it: "▲ 13.2× vs before", "▼ 3 pts", "no change". */
export function kpiDelta(t: T, c: Compared | undefined, kind: KpiKind): { text: string; tone: 'good' | 'bad' | '' } {
  if (kind === 'percent' && c && (c.current === null || c.current === undefined)) {
    return { text: t('insights.reports.kpi.noRatings'), tone: '' };
  }
  if (!c || c.current === null || c.current === undefined || c.previous === null || c.previous === undefined) {
    return { text: '', tone: '' };
  }
  const number = (v: number, digits = 1) => new Intl.NumberFormat(activeLocale(), { maximumFractionDigits: digits })
    .format(Math.abs(v));
  if (kind === 'percent') {
    const pts = c.change_pts;
    if (pts === null || pts === undefined) return { text: '', tone: '' };
    if (pts === 0) return { text: t('insights.reports.kpi.noChange'), tone: '' };
    return { text: `${pts > 0 ? '▲' : '▼'} ${t('insights.reports.kpi.points', { n: ltr(number(pts)) })}`,
             tone: pts > 0 ? 'good' : 'bad' };
  }
  const change = c.change ?? 0;
  if (change === 0) return { text: t('insights.reports.kpi.noChange'), tone: '' };
  const arrow = change > 0 ? '▲' : '▼';
  // More of everything is good news, except money spent.
  const tone = (change > 0) !== (kind === 'money') ? 'good' : 'bad';
  if (!c.previous) return { text: `${arrow} ${t('insights.reports.kpi.new')}`, tone };
  const relative = ltr(c.ratio && c.ratio >= 3
    ? `×${number(c.ratio)}`
    : `${number(c.change_pct ?? 0, Math.abs(c.change_pct ?? 0) >= 100 ? 0 : 1)}%`);
  return { text: `${arrow} ${t('insights.reports.kpi.vsBefore', { change: relative })}`,
           tone };
}

// ---- refusals ---------------------------------------------------------------------

/** A refused request in the person's language (insights.reports.errors.<CODE>). */
export function reportError(t: T, err: unknown): string {
  const code = errorCode(err);
  const worded = code ? t(`insights.reports.errors.${code}`, { defaultValue: '' }) : '';
  return worded || t('insights.reports.errors.generic');
}

/** Why a report did not come out, in words. */
export function statusReason(t: T, reason: string | null): string {
  if (!reason) return '';
  return t(`insights.reports.reason.${reason}`, { defaultValue: t('insights.reports.reason.error') });
}
